import _ from 'lodash';
import { Location } from 'types/location.type';
import { AdjustmentReviewBRLocationState, UwBRLocationReview } from 'types/underwriting.type';
import { SubmissionDetails } from 'types/submission.type';
import { unformatNumberString } from 'helpers/formatters';
import BRPropertyModel from '../views/SubmissionDetails/components/UnderwriterReview/components/BuildersRisk/Property/BRPropertyModel';
import {
    addTriaPremiumToRatedPremium,
    calculatePremiumFromRateAndLimit,
    calculateAnnualizedPremiumFromTotalPremium,
    calculateTriaPremiumFromRatedPremium,
    calculateTriaPremiumFromTotalPremium,
    calculateAnnualizedPremiumFromRateAndLimit,
} from './calculations';

export const getBrTcv = (
    hardCosts: number | undefined,
    softCosts: number | undefined,
    lossOfGrossEarnings: number | undefined,
    lossOfRentalIncome: number | undefined
): number => {
    return (hardCosts || 0) + (softCosts || 0) + (lossOfGrossEarnings || 0) + (lossOfRentalIncome || 0);
};

export const getBrLimitSubtotal = (location?: Location): number => {
    if (!location) return 0;
    return getBrTcv(location.hardCosts, location.softCosts, location.lossOfGrossEarnings, location.lossOfRentalIncome);
};

export const getBrAnnualizedPremiumSubtotal = (
    locationBrReview?: UwBRLocationReview,
    submissionDetails?: SubmissionDetails
): number => {
    if (!locationBrReview || !submissionDetails) return 0;

    return (
        calculateAnnualizedPremiumFromTotalPremium(
            locationBrReview?.hardCostPremium || 0,
            submissionDetails?.effectiveDate,
            submissionDetails.expirationDate
        ) +
        calculateAnnualizedPremiumFromTotalPremium(
            locationBrReview?.softCostPremium || 0,
            submissionDetails?.effectiveDate,
            submissionDetails.expirationDate
        ) +
        calculateAnnualizedPremiumFromTotalPremium(
            locationBrReview?.lossOfRentalIncomePremium || 0,
            submissionDetails?.effectiveDate,
            submissionDetails.expirationDate
        ) +
        calculateAnnualizedPremiumFromTotalPremium(
            locationBrReview?.lossOfGrossEarningsPremium || 0,
            submissionDetails?.effectiveDate,
            submissionDetails.expirationDate
        )
    );
};

export const getBrPremiumSubtotal = (locationBrReview?: UwBRLocationReview): number => {
    if (!locationBrReview) return 0;

    return (
        (locationBrReview?.hardCostPremium || 0) +
        (locationBrReview?.softCostPremium || 0) +
        (locationBrReview?.lossOfRentalIncomePremium || 0) +
        (locationBrReview?.lossOfGrossEarningsPremium || 0)
    );
};

export const getParticipationAdjustedPremiumSubtotal = (
    locationBrReview: UwBRLocationReview,
    participationPercent: number | null
): number | null => {
    if (participationPercent === null || participationPercent === undefined) return null;

    if (participationPercent > 100 || participationPercent < 1) {
        return null;
    }

    const base = getBrPremiumSubtotal(locationBrReview);
    return base * (participationPercent / 100);
};

export const getParticipationAdjustedTotalPremium = (
    locationBrReview: UwBRLocationReview,
    participationPercent: number | null
): number | null => {
    const adjustedBase = getParticipationAdjustedPremiumSubtotal(locationBrReview, participationPercent);
    if (adjustedBase === null) return null;
    // TRIA should be computed from the participation-adjusted base
    const triaPct = unformatNumberString(locationBrReview?.triaPercentage || 0);
    const tria = adjustedBase * (triaPct / 100);
    return Math.ceil(adjustedBase + tria);
};

export const getBrLocationTriaPremium = (locationBrReview: UwBRLocationReview): number => {
    return calculateTriaPremiumFromRatedPremium(
        getBrPremiumSubtotal(locationBrReview),
        unformatNumberString(locationBrReview?.triaPercentage || 0)
    );
};

export const getBrReviewTriaPremiumFromLocations = (
    locations: BRPropertyModel[],
    participationPercent?: number | null
) => {
    if (participationPercent === null || participationPercent === undefined) return 0;
    return Math.ceil(
        locations.reduce((acc, loc) => {
            const adjustedBase = getParticipationAdjustedPremiumSubtotal(loc, participationPercent) || 0;
            const triaPct = unformatNumberString(loc.triaPercentage || 0);
            const tria = adjustedBase * (triaPct / 100);
            return acc + tria;
        }, 0)
    );
};

export const getBrTotalPolicyPremiumFromLocations = (locationReviews: UwBRLocationReview[]): number => {
    return locationReviews.reduce((sum, locationReview) => {
        return sum + addTriaPremiumToRatedPremium(getBrPremiumSubtotal(locationReview), locationReview.triaPercentage);
    }, 0);
};

export const getBrTotalPolicyPremiumFromLocationsWithParticipation = (
    locationReviews: UwBRLocationReview[],
    participationPercent: number | null
): number | null => {
    if (participationPercent === null || participationPercent === undefined) return null;
    return locationReviews.reduce((sum, locationReview) => {
        const adjustedBase = getParticipationAdjustedPremiumSubtotal(locationReview, participationPercent) || 0;
        const triaPct = unformatNumberString(locationReview?.triaPercentage || 0);
        const tria = adjustedBase * (triaPct / 100);
        return Math.ceil(sum + adjustedBase + tria);
    }, 0);
};

export const getBrTotalProgramPremiumFromLocationsWithParticipation = (
    locationReviews: UwBRLocationReview[],
    participationPercent: number | null
): number | null => {
    if (participationPercent === null || participationPercent === undefined) return null;
    return Math.ceil(
        locationReviews.reduce((sum, locationReview) => {
            const adjustedBase = getParticipationAdjustedPremiumSubtotal(locationReview, participationPercent) || 0;
            return sum + adjustedBase;
        }, 0)
    );
};

/*
This function returns a string that can be used for display on an underwriting review. As such the percentage will be xy.ab format.
If used in calculations it should most likely be divided by 100.
 */
export const getReviewLumenParticipationPercent = (
    policyLimitOfLiability: number | null | undefined,
    lumenLimitOfLiability: number | null | undefined
) => {
    return lumenLimitOfLiability && policyLimitOfLiability
        ? ((lumenLimitOfLiability / policyLimitOfLiability) * 100).toFixed(2)
        : '0.00';
};

export const getFinalRateFromAdditionsAndAdjustments = (
    baseRate: number,
    additions: number | undefined,
    adjustments: number | undefined
): number => {
    return (baseRate + (additions || 0)) * (adjustments ? 1 + adjustments : 1);
};

export const calculateBrPricingTable = (
    location: Location,
    locationBrReview: BRPropertyModel,
    submission: SubmissionDetails,
    newTechnicalFinalRate?: number
) => {
    if (locationBrReview.technicalFinalRate === undefined) return {};

    /* -- Calculate technical rates and premiums, which are needed to find final rates and premiums -- */
    const technicalHardCostRate = newTechnicalFinalRate ?? locationBrReview.technicalFinalRate;
    const technicalHardCostPremium = calculatePremiumFromRateAndLimit(
        technicalHardCostRate,
        location.hardCosts,
        submission.effectiveDate || '',
        submission.expirationDate || ''
    );

    const technicalSoftCostRate = (technicalHardCostRate || 0) * (locationBrReview.softCostMultiplier || 0);
    const technicalSoftCostFinalPremium = calculatePremiumFromRateAndLimit(
        technicalSoftCostRate,
        location.softCosts,
        submission.effectiveDate || '',
        submission.expirationDate || ''
    );

    const technicalLossOfRentalIncomeRate =
        (technicalHardCostRate || 0) * (locationBrReview.lossOfRentsMultiplier || 0);
    const technicalLossOfRentalIncomeFinalPremium = calculatePremiumFromRateAndLimit(
        technicalLossOfRentalIncomeRate,
        location.lossOfRentalIncome,
        submission.effectiveDate || '',
        submission.expirationDate || ''
    );

    const technicalLossOfGrossEarningsRate =
        (technicalHardCostRate || 0) * (locationBrReview.lossOfGrossEarningsMultiplier || 0);
    const technicalLossOfGrossEarningsFinalPremium = calculatePremiumFromRateAndLimit(
        technicalLossOfGrossEarningsRate,
        location.lossOfGrossEarnings,
        submission.effectiveDate || '',
        submission.expirationDate || ''
    );

    const technicalPremiumSubtotal =
        technicalHardCostPremium +
        technicalSoftCostFinalPremium +
        technicalLossOfRentalIncomeFinalPremium +
        technicalLossOfGrossEarningsFinalPremium;

    let underwriterAdjustmentFactor = 1;
    let underwriterAdjustedPremium = locationBrReview.underwriterAdjustedPremium;
    if (
        (underwriterAdjustedPremium === undefined || underwriterAdjustedPremium === null) &&
        locationBrReview.technicalFinalRate &&
        locationBrReview.technicalFinalRate > 0 &&
        _.round(locationBrReview.technicalFinalRate, 4) !== _.round(locationBrReview.finalRate || 0, 4)
    ) {
        // This could be a previously save review, in which case we'll have the technical rate but not and adjusted premium.
        // Because the adjusted premium is not persisted in DB, we need to calculate it here.
        underwriterAdjustedPremium = Math.round(getBrPremiumSubtotal(locationBrReview));
    }

    if (underwriterAdjustedPremium && Number(unformatNumberString(underwriterAdjustedPremium)) > 0) {
        // We need to calculate a new rate adjustment based on this underwriter adjusted premium vs. the calculated premium above.
        underwriterAdjustmentFactor = unformatNumberString(underwriterAdjustedPremium) / technicalPremiumSubtotal;
    }

    const finalRate = technicalHardCostRate * underwriterAdjustmentFactor;
    const softCostFinalRate = finalRate * (locationBrReview.softCostMultiplier || 0);
    const lossOfRentalIncomeFinalRate = finalRate * (locationBrReview.lossOfRentsMultiplier || 0);
    const lossOfGrossEarningsFinalRate = finalRate * (locationBrReview.lossOfGrossEarningsMultiplier || 0);

    const hardCostPremium = Math.ceil(
        calculatePremiumFromRateAndLimit(
            finalRate,
            location.hardCosts,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const annualizedHardCostPremium = Math.ceil(
        calculateAnnualizedPremiumFromTotalPremium(
            hardCostPremium,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const softCostPremium = Math.ceil(
        calculatePremiumFromRateAndLimit(
            softCostFinalRate,
            location.softCosts,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const annualizedSoftCostPremium = Math.ceil(
        calculateAnnualizedPremiumFromTotalPremium(
            softCostPremium,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const lossOfRentalIncomePremium = Math.ceil(
        calculatePremiumFromRateAndLimit(
            lossOfRentalIncomeFinalRate,
            location.lossOfRentalIncome,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const annualizedLossOfRentalIncomePremium = Math.ceil(
        calculateAnnualizedPremiumFromTotalPremium(
            lossOfRentalIncomePremium,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const lossOfGrossEarningsPremium = Math.ceil(
        calculatePremiumFromRateAndLimit(
            lossOfGrossEarningsFinalRate,
            location.lossOfGrossEarnings,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );
    const annualizedLossOfGrossEarningsPremium = Math.ceil(
        calculateAnnualizedPremiumFromTotalPremium(
            lossOfGrossEarningsPremium,
            submission.effectiveDate || '',
            submission.expirationDate || ''
        )
    );

    const premiumSubtotal = hardCostPremium + softCostPremium + lossOfRentalIncomePremium + lossOfGrossEarningsPremium;

    return {
        technicalHardCostRate,
        technicalSoftCostRate,
        technicalLossOfGrossEarningsRate,
        technicalLossOfRentalIncomeRate,
        // Premiums
        technicalHardCostPremium,
        technicalSoftCostFinalPremium,
        tehcnicalLossOfRentalIncomeFinalPremium: technicalLossOfRentalIncomeFinalPremium,
        technicalLossOfGrossEarningsFinalPremium,
        technicalPremiumSubtotal,
        // Underwriter adjustments
        underwriterAdjustmentFactor,
        underwriterAdjustedPremium,
        // Final rates with adjustments
        finalRate,
        softCostFinalRate,
        lossOfRentalIncomeFinalRate,
        lossOfGrossEarningsFinalRate,
        // Final premiums with adjustments
        hardCostPremium,
        annualizedHardCostPremium,
        softCostPremium,
        annualizedSoftCostPremium,
        lossOfRentalIncomePremium,
        annualizedLossOfRentalIncomePremium,
        lossOfGrossEarningsPremium,
        annualizedLossOfGrossEarningsPremium,
        premiumSubtotal,
    };
};

export const applyPricingTableToBrLocationReview = (
    locationBrReview: BRPropertyModel,
    pricingTable: any
): BRPropertyModel => {
    return {
        ...locationBrReview,
        technicalFinalRate: pricingTable.technicalHardCostRate,
        finalRate: pricingTable.finalRate,
        hardCostPremium: pricingTable.hardCostPremium,
        softCostFinalRate: pricingTable.softCostFinalRate,
        softCostPremium: pricingTable.softCostPremium,
        lossOfRentalIncomeFinalRate: pricingTable.lossOfRentalIncomeFinalRate,
        lossOfRentalIncomePremium: pricingTable.lossOfRentalIncomePremium,
        lossOfGrossEarningsFinalRate: pricingTable.lossOfGrossEarningsFinalRate,
        lossOfGrossEarningsPremium: pricingTable.lossOfGrossEarningsPremium,
        // Set programPremium from premiumSubtotal (no TRIA)
        programPremium: pricingTable.premiumSubtotal,
        // totalPremium will be set using participation percent by consumers (TopLevelReview)
        totalPremium: undefined,
        underwriterAdjustedPremium: pricingTable.underwriterAdjustedPremium,
    };
};

export const updateDerivedFieldsForBrLocationReview = (
    locationBrReview: BRPropertyModel,
    location: Location,
    submission: SubmissionDetails,
    lumenParticipationPercent: number | null
): void => {
    const rateAdditions =
        (Number(locationBrReview.additionalCatExposure) || 0) +
        (Number(locationBrReview.renovationModifier) || 0) +
        (Number(locationBrReview.midTermPlacement) || 0);

    const rateAdjustment =
        ((Number(locationBrReview.deductibleStructure) || 0) +
            (Number(locationBrReview.contractorQuality) || 0) +
            (Number(locationBrReview.siteSecurity) || 0) +
            (Number(locationBrReview.leadCarrier) || 0) +
            (Number(locationBrReview.constructionModifier) || 0)) /
        100;

    const newTechnicalFinalRate = getFinalRateFromAdditionsAndAdjustments(
        locationBrReview.modeled || locationBrReview.baseRate || 0,
        rateAdditions,
        rateAdjustment
    );

    const pricingTable = calculateBrPricingTable(location, { ...locationBrReview }, submission, newTechnicalFinalRate);

    locationBrReview.totalRateAdditions = rateAdditions;
    locationBrReview.totalRateAdjustment = rateAdjustment;
    // Only set underwriter adjusted premium if it's currently blank
    if (locationBrReview.underwriterAdjustedPremium === undefined) {
        locationBrReview.underwriterAdjustedPremium = pricingTable.underwriterAdjustedPremium;
    }
    locationBrReview.underwriterAdjustmentFactor = pricingTable.underwriterAdjustmentFactor;
    locationBrReview.technicalFinalRate = newTechnicalFinalRate;
    locationBrReview.finalRate = pricingTable.finalRate;
    locationBrReview.softCostFinalRate = pricingTable.softCostFinalRate;
    locationBrReview.lossOfRentalIncomeFinalRate = pricingTable.lossOfRentalIncomeFinalRate;
    locationBrReview.lossOfGrossEarningsFinalRate = pricingTable.lossOfGrossEarningsFinalRate;
    locationBrReview.hardCostPremium = pricingTable.hardCostPremium;
    locationBrReview.softCostPremium = pricingTable.softCostPremium;
    locationBrReview.lossOfRentalIncomePremium = pricingTable.lossOfRentalIncomePremium;
    locationBrReview.lossOfGrossEarningsPremium = pricingTable.lossOfGrossEarningsPremium;
    locationBrReview.programPremium = pricingTable.premiumSubtotal;
    if (lumenParticipationPercent) {
        locationBrReview.totalPremium =
            getParticipationAdjustedTotalPremium(locationBrReview, Number(lumenParticipationPercent)) || undefined;
    }
};
